import { useIsMobile } from "@/hooks/use-mobile";
import { useId, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

/** Collapse an agenda group without changing calendar filters or its events. */
export function AgendaSection({
  title,
  regionLabel,
  count,
  marker,
  open,
  onOpenChange,
  children,
}: {
  title: string;
  regionLabel?: string;
  count: number;
  marker: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  const contentId = useId();
  const mobile = useIsMobile();
  return (
    <section aria-label={regionLabel ?? title} className="min-w-0">
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => onOpenChange(!open)}
          className={`${open ? "mb-3" : ""} flex min-h-11 w-full items-center gap-2 rounded-xl border bg-muted/30 px-3 py-2 text-left text-sm font-medium hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        >
          <span
            aria-hidden="true"
            className="flex w-4 shrink-0 items-center justify-center"
          >
            {marker}
          </span>
          <span className="min-w-0 flex-1">{title}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
            {count}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={`size-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </h3>
      <div
        id={contentId}
        data-mobile-collapse
        data-state={open ? "open" : "closed"}
        hidden={!open && !mobile}
        aria-hidden={!open}
        inert={!open}
      >
        <div>{children}</div>
      </div>
    </section>
  );
}
